import { useGetTemplateDynamicContent } from "@hooks";
import { useCallback, useMemo } from "react";
import { toContextOptions } from "./context-options";
import {
  DynamicNodeProvider,
  type DynamicNodeContextValue,
} from "./dynamic-node-context";
import {
  useConditionEvaluatorBridge,
  useDataKeyRegistration,
} from "./dynamic-node-provider-hooks";
import { buildDynamicNodeResolver } from "./resolution";

interface DynamicNodeTemplatePreviewProviderProps {
  orgId: string;
  /** Device whose characteristics/answers drive resolution; org-scoped when absent. */
  deviceId?: string;
  /** Whether conditionals that evaluate to false should remain visible. */
  showAllConditionals?: boolean;
  /** Set whether conditionals that evaluate to false remain visible. */
  onShowAllConditionalsChange?: (showAll: boolean) => void;
  children: React.ReactNode;
}

const NOOP_SHOW_ALL_CONDITIONALS_CHANGE = () => {};

/**
 * Read-only counterpart of `DynamicNodeEditorProvider` for previewing a bare
 * content template (a "frame") that has no backing document. Nodes register the
 * keys they need exactly as in the editor; values resolve from the org/device's
 * existing answers and characteristics via the template dynamic-content
 * endpoint. Document-bound context keys (identifier, revision, ...) have no
 * source here and render unresolved.
 */
export const DynamicNodeTemplatePreviewProvider = ({
  orgId,
  deviceId,
  showAllConditionals = false,
  onShowAllConditionalsChange = NOOP_SHOW_ALL_CONDITIONALS_CHANGE,
  children,
}: DynamicNodeTemplatePreviewProviderProps) => {
  const { requestedKeys, registerKeys, unregisterKeys } =
    useDataKeyRegistration();

  const { data: dynamicContent, isFetching: loading } =
    useGetTemplateDynamicContent(orgId, deviceId, requestedKeys);

  const isKeyPending = useCallback(
    (key: string): boolean =>
      loading && !(key in (dynamicContent?.dataKeys ?? {})),
    [loading, dynamicContent]
  );

  const value = useMemo<DynamicNodeContextValue>(() => {
    const context = dynamicContent?.context ?? {};
    const resolver = buildDynamicNodeResolver({
      dataKeys: dynamicContent?.dataKeys ?? {},
      context,
    });

    return {
      resolveDataKey: resolver.resolveDataKey,
      resolveContext: resolver.resolveContext,
      contextKeys: toContextOptions(context),
      evaluateHandlebars: resolver.evaluateHandlebars,
      showAllConditionals,
      onShowAllConditionalsChange,
      frozen: false,
      restoredWithCurrentData: false,
      loading,
      registerKeys,
      unregisterKeys,
      isKeyPending,
    };
  }, [
    dynamicContent,
    loading,
    onShowAllConditionalsChange,
    registerKeys,
    showAllConditionals,
    unregisterKeys,
    isKeyPending,
  ]);

  useConditionEvaluatorBridge(value);

  return <DynamicNodeProvider value={value}>{children}</DynamicNodeProvider>;
};

export default DynamicNodeTemplatePreviewProvider;
